import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

interface TokenDistributionChartProps {
  inputTokens: number;
  outputTokens: number;
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs text-foreground-500 mb-0.5">{d.name}</p>
      <p className="text-sm font-semibold" style={{ color: d.payload.fill }}>
        {d.value.toLocaleString()} tokens
      </p>
    </div>
  );
}

export default function TokenDistributionChart({ inputTokens, outputTokens }: TokenDistributionChartProps) {
  const { t } = useTranslation("dashboard");
  const data = [
    { name: t("charts.input"), value: inputTokens },
    { name: t("charts.output"), value: outputTokens },
  ];

  const total = inputTokens + outputTokens;
  const inputPct = total > 0 ? ((inputTokens / total) * 100).toFixed(1) : "0";

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
      <h3 className="font-heading text-lg text-foreground-950 mb-1">{t("charts.tokenSplit")}</h3>
      <p className="text-xs text-foreground-500 mb-3">{t("charts.inputVsOutput")}</p>
      <div className="flex items-center gap-4">
        <div className="w-[140px] h-[140px] flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={65}
                paddingAngle={4}
                dataKey="value"
                strokeWidth={0}
              >
                <Cell fill="oklch(var(--secondary-500))" />
                <Cell fill="oklch(var(--primary-500))" />
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-col gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="w-2.5 h-2.5 rounded-full bg-secondary-500 flex-shrink-0"></span>
              <span className="text-xs text-foreground-500">{t("charts.input")}</span>
            </div>
            <p className="text-sm font-semibold text-foreground-950">{inputTokens.toLocaleString()}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-500 flex-shrink-0"></span>
              <span className="text-xs text-foreground-500">{t("charts.output")}</span>
            </div>
            <p className="text-sm font-semibold text-foreground-950">{outputTokens.toLocaleString()}</p>
          </div>
          <div className="pt-2 border-t border-background-200/50">
            <p className="text-xs text-foreground-400">{t("charts.inputRatio")}</p>
            <p className="text-sm font-bold text-foreground-950">{inputPct}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}